/**
 * RevenueCat Native Service (iOS/Android via Capacitor)
 */

import { Capacitor } from '@capacitor/core';
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import type {
  PurchasesOfferings,
  CustomerInfo,
  PurchasesPackage,
  MakePurchaseResult
} from '@revenuecat/purchases-capacitor';
import { 
  IAPService, 
  IAPOffering, 
  IAPCustomerInfo, 
  IAPPurchaseResult, 
  IAPRestoreResult,
  IAPSubscriptionStatus,
  IAPPackage
} from './types';
import { getSDKKey, REVENUECAT_CONFIG } from './config';
import logger from '../../utils/logger';

class RevenueCatNativeService implements IAPService {
  private isInitialized = false;
  private platform: 'ios' | 'android';

  constructor() {
    this.platform = Capacitor.getPlatform() as 'ios' | 'android';
  }

  async initialize(userId: string): Promise<void> {
    try {
      if (this.isInitialized) {
        logger.info('RevenueCat already initialized');
        return;
      }

      const sdkKey = getSDKKey(this.platform);

      // Configure RevenueCat
      const configObject = {
        apiKey: sdkKey,
        appUserID: userId // Use Firebase Auth UID
      };
      await Purchases.configure(configObject);

      // Set debug logs in development
      if (process.env.NODE_ENV === 'development') {
        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
      }

      this.isInitialized = true;
      logger.info('RevenueCat Native initialized successfully', { platform: this.platform, userId });
    } catch (error) {
      logger.error('Failed to initialize RevenueCat Native', error);
      throw error;
    }
  }

  async getOfferings(): Promise<IAPOffering[]> {
    try {
      const offerings = await Purchases.getOfferings();
      return this.convertOfferings(offerings);
    } catch (error) {
      logger.error('Failed to get offerings', error);
      return [];
    }
  }

  async getCustomerInfo(): Promise<IAPCustomerInfo> {
    try {
      const info = await Purchases.getCustomerInfo();
      return this.convertCustomerInfo(info.customerInfo);
    } catch (error) {
      logger.error('Failed to get customer info', error);
      throw error;
    }
  }

  async purchasePackage(packageId: string): Promise<IAPPurchaseResult> {
    try {
      // Get offerings first to find the package
      const offerings = await Purchases.getOfferings();
      const rcPackage = this.findPackage(offerings, packageId);

      if (!rcPackage) {
        return {
          success: false,
          error: {
            code: 'PACKAGE_NOT_FOUND',
            message: `Package ${packageId} not found`
          }
        };
      }

      // The Capacitor plugin expects the package object directly
      // Just pass the found package as-is since it already has the correct structure
      logger.debug('Attempting purchase with package:', rcPackage.identifier);
      const result: MakePurchaseResult = await Purchases.purchasePackage({ aPackage: rcPackage });

      return {
        success: true,
        customerInfo: this.convertCustomerInfo(result.customerInfo)
      };
    } catch (error: any) {
      logger.error('Purchase failed', error);

      return {
        success: false,
        error: {
          code: error.code || 'UNKNOWN_ERROR',
          message: error.message || 'Purchase failed',
          userCancelled: error.userCancelled || false
        }
      };
    }
  }

  async restorePurchases(): Promise<IAPRestoreResult> {
    try {
      const result = await Purchases.restorePurchases();
      
      return {
        success: true,
        customerInfo: this.convertCustomerInfo(result.customerInfo)
      };
    } catch (error: any) {
      logger.error('Restore purchases failed', error);
      
      return {
        success: false,
        error: {
          code: error.code || 'UNKNOWN_ERROR',
          message: error.message || 'Restore failed'
        }
      };
    }
  }

  async getSubscriptionStatus(): Promise<IAPSubscriptionStatus> {
    try {
      const info = await this.getCustomerInfo();
      
      const isPeakSubscriber = !!info.entitlements.active[REVENUECAT_CONFIG.entitlements.peak];
      const isAdFree = !!info.entitlements.active[REVENUECAT_CONFIG.entitlements.adFree];
      
      return {
        isPeakSubscriber,
        isAdFree: isAdFree || isPeakSubscriber, // Peak includes ad-free
        activeSubscriptions: info.activeSubscriptions,
        entitlements: info.entitlements
      };
    } catch (error) {
      logger.error('Failed to get subscription status', error);
      throw error;
    }
  }

  async logOut(): Promise<void> {
    try {
      await Purchases.logOut();
      this.isInitialized = false;
    } catch (error) {
      logger.error('Failed to log out', error);
      throw error;
    }
  }

  isAvailable(): boolean {
    return Capacitor.isNativePlatform();
  }

  // Helper methods

  private convertOfferings(offerings: PurchasesOfferings): IAPOffering[] {
    const result: IAPOffering[] = [];
    
    if (offerings.current) {
      result.push(this.convertOffering(offerings.current));
    }
    
    // Add other offerings if needed
    Object.entries(offerings.all).forEach(([key, offering]) => {
      if (key !== offerings.current?.identifier) {
        result.push(this.convertOffering(offering));
      }
    });
    
    return result;
  }

  private convertOffering(offering: any): IAPOffering {
    const packages = offering.availablePackages.map((pkg: any) => this.convertPackage(pkg));
    
    return {
      identifier: offering.identifier,
      serverDescription: offering.serverDescription,
      availablePackages: packages,
      monthly: packages.find((p: IAPPackage) => p.packageType === '$rc_monthly'),
      annual: packages.find((p: IAPPackage) => p.packageType === '$rc_annual')
    };
  }

  private convertPackage(pkg: PurchasesPackage): IAPPackage {
    const product = pkg.product;
    
    return {
      identifier: pkg.identifier,
      packageType: pkg.packageType,
      product: {
        identifier: product.identifier,
        priceString: product.priceString,
        price: product.price,
        title: product.title,
        description: product.description,
        currencyCode: product.currencyCode,
        introPrice: product.introPrice ? {
          priceString: product.introPrice.priceString,
          price: product.introPrice.price,
          cycles: product.introPrice.cycles,
          period: product.introPrice.period
        } : undefined
      }
    };
  }

  private convertCustomerInfo(info: CustomerInfo): IAPCustomerInfo {
    const entitlements: IAPCustomerInfo['entitlements'] = {
      all: {},
      active: {}
    };
    
    // Convert entitlements
    Object.entries(info.entitlements.all).forEach(([key, entitlement]) => {
      const converted = {
        identifier: entitlement.identifier,
        isActive: entitlement.isActive,
        willRenew: entitlement.willRenew,
        expirationDate: entitlement.expirationDate || undefined,
        productIdentifier: entitlement.productIdentifier
      };
      
      entitlements.all[key] = converted;
      
      if (entitlement.isActive) {
        entitlements.active[key] = converted;
      }
    });
    
    return {
      activeSubscriptions: info.activeSubscriptions,
      allPurchasedProductIdentifiers: info.allPurchasedProductIdentifiers,
      entitlements,
      firstSeen: info.firstSeen,
      originalAppUserId: info.originalAppUserId,
      originalApplicationVersion: info.originalApplicationVersion || undefined
    };
  }

  private findPackage(offerings: PurchasesOfferings, packageId: string): PurchasesPackage | null {
    // First check current offering
    if (offerings.current) {
      const pkg = offerings.current.availablePackages.find(p => p.identifier === packageId);
      if (pkg) return pkg;
    }
    
    // Check all offerings
    for (const offering of Object.values(offerings.all)) {
      const pkg = offering.availablePackages.find(p => p.identifier === packageId);
      if (pkg) return pkg;
    }
    
    return null;
  }
}

export const revenueCatNativeService = new RevenueCatNativeService();